import React, { useState } from 'react';
import { Modal, Form, Input, Button, message, Descriptions, Typography } from 'antd';
import { CloudUploadOutlined, CopyOutlined } from '@ant-design/icons';
import { apiService } from '../services/api';
import { useAuth } from '../hooks/useAuth';

const { Text } = Typography;

interface ShareManagerProps {
  record: any;
  onUpdate: () => void;
  buttonType?: 'primary' | 'text' | 'link' | 'default' | 'dashed';
  iconOnly?: boolean;
  size?: 'large' | 'middle' | 'small';
  style?: React.CSSProperties;
}

export const ShareManager: React.FC<ShareManagerProps> = ({ record, onUpdate, buttonType = 'primary', iconOnly = false, size, style }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [loadingShare, setLoadingShare] = useState(false);
  const [form] = Form.useForm();
  const { user } = useAuth();

  const handleCreateShare = async (values: any) => {
    if (!record?.id) return;
    
    setLoadingShare(true);
    try {
      await apiService.createNextcloudShare(Number(record.id), values.email, values.name);
      message.success('Share created successfully');
      setIsModalVisible(false);
      onUpdate();
    } catch (error) {
      console.error(error);
      message.error('Failed to create share');
    } finally {
      setLoadingShare(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    message.success('Copied to clipboard');
  };

  const showModal = () => {
    if (!record?.nextcloudUploadLink) {
        const userName = user?.name && user?.surname ? `${user.name} ${user.surname}` : user?.username || '';
        form.setFieldsValue({
            name: userName,
            email: user?.email || '',
        });
    }
    setIsModalVisible(true);
  };

  const renderContent = () => {
    if (record?.nextcloudUploadLink) {
        return (
            <Descriptions bordered column={1}>
                <Descriptions.Item label="Upload Link">
                    <a href={record.nextcloudUploadLink} target="_blank" rel="noopener noreferrer" style={{ marginRight: 10 }}>{record.nextcloudUploadLink}</a>
                    <Button type="text" icon={<CopyOutlined />} onClick={() => copyToClipboard(record.nextcloudUploadLink)} />
                </Descriptions.Item>
                <Descriptions.Item label="Password">
                    <Text code style={{ marginRight: 10 }}>{record.nextcloudSharePassword}</Text>
                    <Button type="text" icon={<CopyOutlined />} onClick={() => copyToClipboard(record.nextcloudSharePassword)} />
                </Descriptions.Item>
                <Descriptions.Item label="Expiration">{record.nextcloudShareExpiration}</Descriptions.Item>
            </Descriptions>
        );
    }

    return (
        <Form form={form} layout="vertical" onFinish={handleCreateShare}>
            <Form.Item name="name" label="Recipient Name" rules={[{ required: true, message: 'Please enter recipient name' }]}>
                <Input placeholder="e.g. John Doe" />
            </Form.Item>
            <Form.Item name="email" label="Recipient Email" rules={[{ required: true, type: 'email', message: 'Please enter a valid email' }]}>
                <Input placeholder="e.g. john@example.com" />
            </Form.Item>
            <div style={{ textAlign: 'right' }}>
                <Button onClick={() => setIsModalVisible(false)} style={{ marginRight: 8 }}>Cancel</Button>
                <Button type="primary" htmlType="submit" loading={loadingShare}>
                    Create Share
                </Button>
            </div>
        </Form>
    );
  };

  return (
    <>
      <Button 
        type={buttonType} 
        icon={<CloudUploadOutlined />} 
        onClick={showModal}
        title="Upload Data / Manage Share"
        size={size}
        style={style}
      >
        {!iconOnly && "Upload Files"}
      </Button>

      <Modal
        title={record?.nextcloudUploadLink ? "Nextcloud Share Details" : "Create Nextcloud Share"}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={record?.nextcloudUploadLink ? 600 : 520}
      >
        {renderContent()}
      </Modal>
    </>
  );
};



